// src/helpers/useTextHighlighter.js
import { ref } from 'vue'

// Safe, SSR-friendly flag
const HL_DEBUG = !!(
  typeof window !== 'undefined' &&
  window.localStorage &&
  window.localStorage.getItem('HL_DEBUG') === '1'
)

function dlog(...args) {
  if (HL_DEBUG) console.log('[HL]', ...args) // eslint-disable-line no-console
}

if (HL_DEBUG) {
  // eslint-disable-next-line no-console
  console.info('[HL] useTextHighlighter module loaded')
}

// function isCoarsePointer() {
//   try {
//     return window.matchMedia && window.matchMedia('(pointer: coarse)').matches
//   } catch {
//     return false
//   }
// }

export function useTextHighlighter(containerRef, opts = {}) {
  const showToolbar = ref(false)
  const selectionIsValid = ref(false)
  const mode = ref('add') // 'add' | 'remove'
  const x = ref(0)
  const y = ref(0)
  const selectedRange = ref(null)
  const activeHighlightEl = ref(null)
  const highlightClass = 'highlighted'
  const onChange = opts.onChange || (() => {})
  const toolbarEl = ref(null)
  const shouldWrap = ref(false)
  const placement = ref('above')

  // dlog('init highlighter')

  // let parent register the toolbar element
  function registerToolbarEl(el) {
    toolbarEl.value = el || null
  }

  // update XY to avoid edges and decide wrapping/placement
  function adjustToolbarPosition(rect) {
    const el = toolbarEl.value
    const pad = 8
    const vw = window.innerWidth || document.documentElement.clientWidth
    const vh = window.innerHeight || document.documentElement.clientHeight

    // If no element yet, best-effort clamp just by x center
    let cx = x.value
    const maxX = vw - pad
    if (cx < pad) cx = pad
    if (cx > maxX) cx = maxX

    let tW = el?.offsetWidth || 0
    let tH = el?.offsetHeight || 0

    // If the toolbar is wider than the screen, enable wrap and remeasure once.
    if (el && tW > vw - pad * 2) {
      shouldWrap.value = true
      // next frame measure again
      tW = el.offsetWidth
      tH = el.offsetHeight
    } else {
      shouldWrap.value = false
    }

    // clamp x such that centered toolbar never leaves viewport
    const half = tW ? tW / 2 : 0
    const minX = pad + half
    const maxCenterX = vw - pad - half
    if (half) {
      if (cx < minX) cx = minX
      if (cx > maxCenterX) cx = maxCenterX
    }
    x.value = cx

    // Decide above/below based on available space
    // current logic places toolbar above (y = rect.top)
    // flip to below if not enough room above
    const needBelow = el && rect.top - (tH + 12) < 0
    placement.value = needBelow ? 'below' : 'above'
    y.value = needBelow ? rect.bottom : rect.top

    // Also guard bottom overflow when placed below
    if (placement.value === 'below' && el) {
      const bottomY = y.value + tH + 8
      if (bottomY > vh) {
        // not enough space below either: fall back to above
        placement.value = 'above'
        y.value = rect.top
      }
    }
  }

  // Works with either a raw element ref or a Vuetify component ref.
  function getContainerEl() {
    const cmp = containerRef?.value
    return (cmp && cmp.$el) || cmp || null
  }

  function closestHighlight(node) {
    if (!node) return null
    return node.nodeType === 1 && node.classList?.contains(highlightClass)
      ? node
      : (node.parentElement &&
          node.parentElement.closest?.('.' + highlightClass)) ||
          null
  }

  function closestBlock(node) {
    if (!node || node.nodeType !== 1) node = node?.parentElement || null
    return (
      node?.closest?.(
        'p,li,blockquote,h1,h2,h3,h4,h5,h6,div[contenteditable],div[data-block]'
      ) || getContainerEl()
    )
  }

  function computeXY() {
    const range = selectedRange.value
    if (!range) return

    let rect = range.getBoundingClientRect()
    if ((rect.width < 1 && rect.height < 1) || Number.isNaN(rect.left)) {
      const r = range.startContainer?.getClientRects?.()[0]
      if (r) rect = r
    }
    if (!rect || !isFinite(rect.left)) return

    // Viewport coords for a fixed-position toolbar
    let cx = rect.left + rect.width / 2
    let cy = rect.top

    // keep inside screen a bit (padding 8px)
    const pad = 8
    const maxX =
      (window.innerWidth || document.documentElement.clientWidth) - pad
    if (cx < pad) cx = pad
    if (cx > maxX) cx = maxX

    x.value = cx
    y.value = cy // your CSS arrow already offsets upward via transform
  }

  // --- added helper ---
  function nearestAllowedBlock(node) {
    let el = node?.nodeType === 1 ? node : node?.parentElement
    if (!el) return null

    // must be inside a .p-content-container OR #footer-section
    const section = el.closest?.('.p-content-container, #footer-section')
    if (!section) return null

    // 1) <p>
    const p = el.closest?.('p')
    if (p && section.contains(p)) return p

    // 2) <li>
    const li = el.closest?.('li')
    if (li && section.contains(li)) return li

    // 3) .study-question-statement
    const sq = el.closest?.('.study-question-statement')
    if (sq && section.contains(sq)) return sq

    // 4) .verse-container > div:first-child
    const verse = el.closest?.('.verse-container')
    if (verse && section.contains(verse)) {
      const firstDiv = verse.querySelector(':scope > div')
      if (firstDiv && firstDiv.contains(el)) return firstDiv
    }

    return null
  }

  /**
   * Rebuild selection inside a block by matching an excerpt across all text nodes.
   * Returns true if successful.
   */
  function setSelectionFromText(blockId, excerpt) {
    dlog('rebuild selection', { blockId, excerptLen: (excerpt || '').length })
    const containerEl = getContainerEl()
    if (!containerEl || !blockId || !excerpt) return false

    const blockEl = document.getElementById(blockId)
    if (!blockEl || !containerEl.contains(blockEl)) return false

    // Build a normalized text stream and a pointer map back to raw node positions
    const pointers = [] // each entry: { node, rawOffset }
    let normText = ''

    const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.nodeValue && node.nodeValue.trim()
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP
      },
    })

    const pushChar = (node, rawIdx, ch) => {
      normText += ch
      // map this normalized char index to the raw node+offset
      pointers.push({ node, rawOffset: rawIdx })
    }

    // normalize whitespace to single spaces while building pointers
    while (walker.nextNode()) {
      const node = walker.currentNode
      const raw = node.nodeValue

      let prevWasSpace =
        normText.length > 0 && /\s/.test(normText[normText.length - 1])
      for (let i = 0; i < raw.length; i++) {
        const ch = raw[i]
        if (/\s/.test(ch)) {
          if (!prevWasSpace) {
            pushChar(node, i, ' ')
            prevWasSpace = true
          }
        } else {
          pushChar(node, i, ch)
          prevWasSpace = false
        }
      }
    }

    // Nothing to search against
    if (!normText) return false

    // Normalize excerpt the same way
    const normExcerpt = String(excerpt).replace(/\s+/g, ' ').trim()
    if (!normExcerpt) return false

    const startIdx = normText.indexOf(normExcerpt)
    if (startIdx === -1) return false

    const endExclusive = startIdx + normExcerpt.length

    // Map normalized positions back to raw node positions
    const mapStart = pointers[startIdx]
    const mapEndChar = pointers[endExclusive - 1] // last char included
    if (!mapStart || !mapEndChar) return false

    const startNode = mapStart.node
    const startOffset = mapStart.rawOffset
    const endNode = mapEndChar.node
    const endOffset = mapEndChar.rawOffset + 1 // Range end is exclusive

    // Build and set selection
    const sel = window.getSelection && window.getSelection()
    if (!sel) return false

    const range = document.createRange()
    try {
      range.setStart(startNode, startOffset)
      range.setEnd(endNode, endOffset)
    } catch (_e) {
      return false
    }

    sel.removeAllRanges()
    sel.addRange(range)

    // also sync our composable’s state so applyHighlight() can proceed
    selectedRange.value = range

    return true
  }

  function rangeHasCommentNode(range) {
    if (!range || !range.commonAncestorContainer) return false
    const root = range.commonAncestorContainer
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT, {
      acceptNode(node) {
        try {
          return range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_SKIP
        } catch {
          return NodeFilter.FILTER_SKIP
        }
      },
    })
    return !!walker.nextNode()
  }

  // --- end added helper ---

  // Replace your handleMouseUp() with this version
  function handleMouseUp() {
    dlog('handleMouseUp fired')

    const sel = window.getSelection?.()
    if (!sel) {
      dlog('no window selection object')
      // keep previously set range (e.g. from tapping a highlight)
      if (selectedRange.value) return
      return bail()
    }
    if (!sel.rangeCount) {
      dlog('rangeCount=0')
      if (selectedRange.value) return
      return bail()
    }
    if (sel.isCollapsed) {
      dlog('selection collapsed')
      if (selectedRange.value) return
      return bail()
    }

    const range = sel.getRangeAt(0)

    // 🚧 Avoid ranges that include Vue’s comment anchors
    if (rangeHasCommentNode(range)) {
      dlog(
        'selection intersects Vue comment anchors; bailing to avoid patch crash'
      )
      return bail()
    }

    const containerEl = getContainerEl()
    if (!containerEl) {
      dlog('no containerEl')
      return bail()
    }

    // ancestor in allowed sections?
    const startNode =
      range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement

    const pContainer = startNode?.closest?.(
      '.p-content-container, #footer-section'
    )
    if (!pContainer) {
      dlog('not in allowed section')
      return bail()
    }
    if (!containerEl.contains(pContainer)) {
      dlog('section not under container')
      return bail()
    }

    const startBlock = nearestAllowedBlock(range.startContainer)
    const endBlock = nearestAllowedBlock(range.endContainer)
    dlog('blocks', {
      startBlock: !!startBlock && startBlock.tagName,
      endBlock: !!endBlock && endBlock.tagName,
      same: startBlock === endBlock,
      id: startBlock && startBlock.id,
    })

    if (!startBlock || !endBlock || startBlock !== endBlock) {
      dlog('blocks failed guard')
      return bail()
    }

    selectedRange.value = range
    mode.value = closestHighlight(range.startContainer?.parentNode)
      ? 'remove'
      : 'add'
    dlog('selection OK', { mode: mode.value })

    requestAnimationFrame(() => {
      computeXY()
      const rect = range.getBoundingClientRect()
      const willShow = !(rect.width < 1 && rect.height < 1)
      selectionIsValid.value = !!willShow
      showToolbar.value = !!willShow
      if (willShow) adjustToolbarPosition(rect)
    })
  }

  function bail() {
    showToolbar.value = false
    selectedRange.value = null
    return
  }

  function hideToolbar() {
    showToolbar.value = false
    selectedRange.value = null
    try {
      window.getSelection?.().removeAllRanges()
    } catch (_e) {
      // ignore:
    }
  }

  // Wraps arbitrary contents (can span multiple nodes) safely.
  // function wrapRange(range) {
  //   const wrapper = document.createElement('span')
  //   wrapper.className = highlightClass
  //   wrapper.style.backgroundColor = '#ffeb3b'
  //   wrapper.style.color = '#000'
  //   wrapper.style.display = 'inline' // avoid weird line splits

  //   const contents = range.extractContents()
  //   wrapper.appendChild(contents)
  //   range.insertNode(wrapper)

  //   return wrapper
  // }

  // Merge adjacent text nodes inside a block after DOM surgery.
  function normalizeAround(node) {
    const block = closestBlock(node) || node.parentElement
    if (block && block.normalize) block.normalize()
  }

  function isRangeInDom(range, root) {
    if (!range || !root) return false
    const startNode =
      range.startContainer && range.startContainer.ownerDocument
        ? range.startContainer
        : null
    const endNode =
      range.endContainer && range.endContainer.ownerDocument
        ? range.endContainer
        : null
    if (!startNode || !endNode) return false
    const startEl =
      startNode.nodeType === 1 ? startNode : startNode.parentElement
    const endEl = endNode.nodeType === 1 ? endNode : endNode.parentElement
    if (!startEl || !endEl) return false
    return root.contains(startEl) && root.contains(endEl)
  }

  function hexToRgba(hex, alpha = 0.5) {
    const m =
      hex && hex.trim().match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
    if (!m) return hex
    const r = parseInt(m[1], 16),
      g = parseInt(m[2], 16),
      b = parseInt(m[3], 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  function applyHighlight(colorHex = '#ffeb3b') {
    const containerEl = getContainerEl()
    if (!selectedRange.value || !containerEl) {
      dlog('apply: no selectedRange or container')
      return
    }

    if (!isRangeInDom(selectedRange.value, containerEl)) {
      dlog(
        'apply: stale range, attempting to recover from window.getSelection()'
      )
      const sel = window.getSelection?.()
      if (!sel || !sel.rangeCount) {
        dlog('apply: no window selection to recover')
        return
      }
      selectedRange.value = sel.getRangeAt(0).cloneRange()
      if (!isRangeInDom(selectedRange.value, containerEl)) {
        dlog('apply: recovery failed')
        return
      }
    }

    dlog('apply: proceeding', { colorHex })

    const range = selectedRange.value.cloneRange()

    // 🚧 Final safety before mutating DOM
    if (rangeHasCommentNode(range)) {
      dlog(
        'apply: range intersects Vue comment anchors; aborting to avoid insertBefore crash'
      )
      hideToolbar()
      return
    }

    try {
      const wrapper = document.createElement('span')
      wrapper.className = highlightClass
      wrapper.style.backgroundColor = hexToRgba(colorHex, 0.5) // 50% bg, solid text
      wrapper.style.display = 'inline'
      wrapper.setAttribute('data-highlight-color', colorHex)

      const contents = range.extractContents()
      wrapper.appendChild(contents)
      range.insertNode(wrapper)

      normalizeAround(wrapper)
      hideToolbar()

      // 🚦 defer persistence to next macrotick (prevents Vue patch crashes)
      setTimeout(() => {
        try {
          onChange('highlight', { range: selectedRange.value, wrapper })
        } catch (e) {
          console.warn('highlight save failed:', e)
        }
      }, 0)
    } catch (e) {
      console.warn('applyHighlight failed:', e)
      hideToolbar()
    }
  }

  function unwrap(el) {
    const frag = document.createDocumentFragment()
    while (el.firstChild) frag.appendChild(el.firstChild)
    el.replaceWith(frag)
    return frag
  }

  function recolorHighlight(colorHex = '#ffeb3b') {
    const containerEl = getContainerEl()
    if (!containerEl) return

    // 1) Direct node path (tap/clicked highlight)
    const direct = activeHighlightEl?.value || null
    if (direct && containerEl.contains(direct)) {
      // apply color to the tapped span
      const rgba = hexToRgba(colorHex, 0.5)
      direct.dataset && (direct.dataset.highlightColor = colorHex)
      direct.style.backgroundColor = rgba

      // find paragraph for persistence
      const paragraphEl =
        nearestAllowedBlock(direct) ||
        closestBlock(direct) ||
        direct.closest?.('[id]') ||
        null
      const key = paragraphEl?.id || null

      hideToolbar()
      activeHighlightEl.value = null

      setTimeout(() => {
        if (paragraphEl && key) {
          try {
            onChange('highlight', { paragraphEl, key })
          } catch (e) {
            console.warn('[HL] recolor save failed:', e)
          }
        }
      }, 0)
      return
    }

    // 2) Fallback to selection-based recolor (drag-selected highlight)
    let range = selectedRange.value
    if (!range || !isRangeInDom(range, containerEl)) {
      const sel = window.getSelection?.()
      if (!sel || !sel.rangeCount) return hideToolbar()
      range = sel.getRangeAt(0)
      if (!isRangeInDom(range, containerEl)) return hideToolbar()
    }

    // collect target highlight nodes intersecting the range
    const targets = new Set()
    const first = closestHighlight(
      range.startContainer?.parentNode || range.startContainer
    )
    if (first) targets.add(first)

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode(node) {
          return node.nodeType === 1 &&
            node.classList?.contains(highlightClass) &&
            range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_SKIP
        },
      }
    )
    let n
    while ((n = walker.nextNode())) targets.add(n)

    // If nothing intersected (rare), try the nearest highlight at anchor
    if (!targets.size) {
      const alt = closestHighlight(
        range.startContainer?.parentNode || range.startContainer
      )
      if (alt) targets.add(alt)
    }
    if (!targets.size) return hideToolbar()

    // apply color
    const rgba = hexToRgba(colorHex, 0.5)
    targets.forEach((el) => {
      el.dataset && (el.dataset.highlightColor = colorHex)
      el.style.backgroundColor = rgba
    })

    // persist once for the paragraph that contains the first target
    const any = targets.values().next().value
    const paragraphEl =
      nearestAllowedBlock(any) ||
      closestBlock(any) ||
      any.closest?.('[id]') ||
      null
    const key = paragraphEl?.id || null

    hideToolbar()

    setTimeout(() => {
      if (paragraphEl && key) {
        try {
          onChange('highlight', { paragraphEl, key })
        } catch (e) {
          console.warn('[HL] recolor save failed:', e)
        }
      }
    }, 0)
  }

  function removeHighlight() {
    const containerEl = getContainerEl()
    if (!containerEl) {
      dlog('remove: no container')
      return
    }

    // 1) Direct node path (tap/clicked highlight)
    const direct = activeHighlightEl?.value || null
    if (direct && containerEl.contains(direct)) {
      const paragraphEl =
        nearestAllowedBlock(direct) ||
        closestBlock(direct) ||
        direct.closest?.('[id]') ||
        null
      const key = paragraphEl?.id || null

      unwrap(direct)
      normalizeAround(paragraphEl || direct)
      hideToolbar()
      activeHighlightEl.value = null

      setTimeout(() => {
        if (paragraphEl && key) {
          try {
            onChange('remove', { paragraphEl, key })
          } catch (e) {
            console.warn(e)
          }
        }
      }, 0)
      return
    }

    // 2) Fallback to selection-based removal (drag-selected highlight)
    let range = selectedRange.value
    if (!range || !isRangeInDom(range, containerEl)) {
      dlog('remove: stale or empty range, trying window.getSelection()')
      const sel = window.getSelection?.()
      if (!sel || !sel.rangeCount) {
        dlog('remove: no window selection')
        return hideToolbar()
      }
      range = sel.getRangeAt(0)
      if (!isRangeInDom(range, containerEl)) {
        dlog('remove: selection not in container')
        return hideToolbar()
      }
    }

    dlog('remove: proceeding (selection path)')

    // collect targets intersecting the range
    const toRemove = new Set()
    const first = closestHighlight(
      range.startContainer?.parentNode || range.startContainer
    )
    if (first) toRemove.add(first)

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode(node) {
          return node.nodeType === 1 &&
            node.classList?.contains(highlightClass) &&
            range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_SKIP
        },
      }
    )
    let n
    while ((n = walker.nextNode())) toRemove.add(n)

    if (!toRemove.size) {
      hideToolbar()
      return
    }

    // paragraph/key for saving
    const any = toRemove.values().next().value
    const paragraphEl =
      nearestAllowedBlock(any) ||
      closestBlock(any) ||
      any.closest?.('[id]') ||
      null
    const key = paragraphEl?.id || null

    // unwrap now
    toRemove.forEach((node) => unwrap(node))
    normalizeAround(paragraphEl || range.commonAncestorContainer)
    hideToolbar()

    setTimeout(() => {
      if (paragraphEl && key) {
        try {
          onChange('remove', { paragraphEl, key })
        } catch (e) {
          console.warn(e)
        }
      }
    }, 0)
  }

  // Reposition while selection is active; hide if selection cleared.
  function handleScrollOrResize() {
    if (!selectedRange.value) {
      showToolbar.value = false
      return
    }
    computeXY()
    try {
      const rect = selectedRange.value.getBoundingClientRect()
      adjustToolbarPosition(rect)
    } catch {
      // <— this empty block triggers no-empty
    }
  }

  function showToolbarForHighlightNode(node) {
    // allow passing the span or any child inside it
    const hl = node?.closest?.('.' + highlightClass) || node
    if (!hl || !hl.classList?.contains(highlightClass)) return false

    // build a range around the highlight so recolor/remove work
    const range = document.createRange()
    try {
      range.selectNodeContents(hl)
    } catch {
      return false
    }

    selectedRange.value = range
    activeHighlightEl.value = hl // 👈 track the tapped highlight
    mode.value = 'remove'

    // position the toolbar over the highlight
    const rect = hl.getBoundingClientRect()
    x.value = rect.left + rect.width / 2
    y.value = rect.top
    selectionIsValid.value = true
    showToolbar.value = true

    // clamp/wrap/flip as needed
    requestAnimationFrame(() => adjustToolbarPosition(rect))
    return true
  }

  return {
    showToolbar,
    selectionIsValid,
    mode,
    x,
    y,
    handleMouseUp,
    handleScrollOrResize,
    applyHighlight,
    removeHighlight,
    setSelectionFromText,
    registerToolbarEl,
    shouldWrap,
    placement,
    recolorHighlight,
    showToolbarForHighlightNode,
    hideToolbar,
  }
}
